<template>
  <header>
    <nav class="navbar navbar-expand-md navbar-light">
      <div class="container-fluid">
        <router-link to="/" class="navbar-brand">
          <img
            :src="$t('Header.BrandLogo')"
            alt="Brand Logo"
            class="img-fluid"
          />
        </router-link>

        <button
          class="navbar-toggler"
          type="button"
          data-toggle="collapse"
          data-target="#navbar-Nav"
          @click="toggleCollapse"
          aria-controls="navbar-Nav"
          aria-expanded="false"
          aria-label="Toggle navigation"
          :class="{ active: isCollapsed }"
        ></button>

        <div
          :class="{ show: isCollapsed }"
          class="collapse navbar-collapse my-2"
          id="navbar-Nav"
        >
          <ul class="navbar-nav ms-md-auto">
            <!-- Home Dropdown Submenu -->
            <li 
              class="nav-item dropdown" 
              @mouseenter="openHome" 
              @mouseleave="closeHome"
            >
              <a
                class="nav-link dropdown-toggle"
                href="#"
                role="button"
                @click.prevent="toggleHome"
                :class="{ active: isHomeActive || homeDropdownOpen }"
              >
                {{ $t('Header.HomeDropdown') }}
              </a>
              <ul class="dropdown-menu" :class="{ show: homeDropdownOpen }">
                <li>
                  <router-link
                    to="/Home-V1"
                    class="dropdown-item"
                    active-class="active"
                    @click="selectHome"
                  >
                    {{ $t('Header.HomeV1Tab') }}
                  </router-link>
                </li>
                <li>
                  <router-link
                    to="/Home-V2"
                    class="dropdown-item"
                    active-class="active"
                    @click="selectHome"
                  >
                    {{ $t('Header.HomeV2Tab') }}
                  </router-link>
                </li>
              </ul>
            </li>

            <li class="nav-item">
              <router-link
                to="/SignIn"
                class="nav-link"
                active-class="active" @click="closeMenu"
              >
                {{ $t('Header.SignInTab') }}
              </router-link>
            </li>

            <li class="nav-item">
              <router-link to="/FAQ" class="nav-link" @click="closeMenu">
                {{ $t('Header.SupportTab') }}
              </router-link>
            </li>
          </ul>
          
          <!-- Language Toggle -->
          <div class="lang-toggle-container ms-md-3 my-2 my-md-0">
            <button 
              class="lang-btn" 
              :class="{ active: currentLocale === 'en' }" 
              @click="changeLang('en')"
            >
              EN
            </button>
            <span class="lang-divider">/</span>
            <button 
              class="lang-btn" 
              :class="{ active: currentLocale === 'fr' }" 
              @click="changeLang('fr')"
            >
              FR
            </button>
          </div>
        </div>
      </div>
    </nav>
  </header>

  <div v-if="isHome" class="home-banner-wrapper">
    <banner></banner>
  </div>
</template>

<script>
import banner from '../UI/Banner.vue';
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { loadLocaleMessages } from '../../main.js';

export default {
  components: { banner },
  setup() {
    const isCollapsed = ref(false); 
    const route = useRoute();
    const homeDropdownOpen = ref(false);
    const { locale } = useI18n();

    const currentLocale = computed(() => locale.value);

    const changeLang = async (lang) => {
      await loadLocaleMessages(lang);
      locale.value = lang;
      localStorage.setItem('locale', lang);
    };

    const isHomeActive = computed(() => {
      return route.path === '/Home-V1' || route.path === '/Home-V2';
    });

    const isHome = computed(() => {
      return route.path === '/Home-V2' || route.path === '/Thanks' || route.path === '/SignIn';
    });

    const toggleCollapse = () => {
      isCollapsed.value = !isCollapsed.value;
      document.body.classList.toggle('no-scroll', isCollapsed.value)
    };

    const closeMenu = () => {
      isCollapsed.value = false;
      homeDropdownOpen.value = false;
      document.body.classList.remove('no-scroll');
    };

    const toggleHome = () => {
      homeDropdownOpen.value = !homeDropdownOpen.value;
    };
    const openHome = () => {
      if (window.innerWidth >= 768) {
        homeDropdownOpen.value = true;
      }
    };
    const closeHome = () => {
      if (window.innerWidth >= 768) {
        homeDropdownOpen.value = false;
      }
    };
    const selectHome = () => {
      homeDropdownOpen.value = false;
      closeMenu();
    };

    return { 
      isCollapsed, 
      toggleCollapse, 
      closeMenu, 
      isHome,
      homeDropdownOpen,
      isHomeActive,
      toggleHome,
      openHome,
      closeHome,
      selectHome,
      currentLocale,
      changeLang
    };
  },
};
</script>

<style scoped>
/* Dropdown wrapper styling */
.nav-item.dropdown {
  position: relative;
}

.dropdown-toggle::after {
  display: inline-block;
  margin-left: 0.3em;
  vertical-align: middle;
  content: "";
  border-top: 0.3em solid;
  border-right: 0.3em solid transparent;
  border-bottom: 0;
  border-left: 0.3em solid transparent;
  transition: transform 0.2s ease;
}

.dropdown-toggle.active::after,
.dropdown-toggle:hover::after {
  border-top-color: #2584C5 !important;
}

.dropdown-menu {
  display: none;
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #ffffff;
  min-width: 160px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.5rem 0;
  margin-top: 0;
  z-index: 1000;
  list-style: none;
}

/* Bridge the gap between parent nav item and dropdown menu to prevent hover loss */
.dropdown-menu::before {
  content: '';
  position: absolute;
  top: -15px;
  left: 0;
  right: 0;
  height: 15px;
  background: transparent;
}

.dropdown-menu.show {
  display: block;
  animation: dropdownFadeIn 0.2s ease;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 0.6rem 1.5rem;
  clear: both;
  font-weight: 500;
  color: #717d96;
  text-align: center;
  white-space: nowrap;
  background-color: transparent;
  border: 0;
  transition: all 0.2s ease;
  font-size: 15px;
  text-decoration: none !important;
}

.dropdown-item:hover, .dropdown-item:focus {
  color: #2584C5 !important;
  background-color: #f8fafc;
  text-decoration: none !important;
}

.dropdown-item.active, .dropdown-item:active {
  color: #2584C5 !important;
  background-color: #f1f5f9;
  font-weight: 700;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translate(-50%, 10px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

/* Pure CSS hover behavior for desktop window viewports */
@media screen and (min-width: 768px) {
  .nav-item.dropdown:hover .dropdown-menu {
    display: block !important;
  }
}

@media screen and (max-width: 767px) {
  .dropdown-menu {
    position: static;
    float: none;
    transform: none;
    box-shadow: none;
    border: none;
    background-color: #f8fafc;
    margin-top: 0.25rem;
    padding: 0.5rem 0;
    width: 100%;
    animation: none;
  }
  
  .dropdown-menu.show {
    display: block;
  }

  .dropdown-item {
    text-align: center;
    padding: 0.75rem 1.5rem;
  }
}

/* Language Toggle styling */
.lang-toggle-container {
  display: flex;
  align-items: center;
  gap: 2px;
  border: 1px solid #e2e8f0;
  border-radius: 50px;
  padding: 2px 6px;
  background-color: #f8fafc;
}

.lang-btn {
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 700;
  color: #717d96;
  padding: 4px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 50px;
  outline: none;
}

.lang-btn:hover {
  color: #2584C5;
}

.lang-btn.active {
  color: #ffffff;
  background-color: #2584C5;
  box-shadow: 0 2px 6px rgba(37, 132, 197, 0.2);
}

.lang-divider {
  color: #cbd5e1;
  font-size: 13px;
  user-select: none;
}
</style>
